import { motion } from 'framer-motion'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FieldValues, useFormContext } from 'react-hook-form'
import { Button } from '@demandlocal/components'
import { useAtom } from 'jotai'
import { createCampaignFormValuesAtom } from '../../InventoryCampaign/NewCampaign/CreateNewCampaignForm'
import { ReactComponent as MobileIcon } from '../../../images/icons/mobile_product.svg'
import { ReactComponent as OttIcon } from '../../../images/icons/ott_product.svg'
import { ReactComponent as VideoIcon } from '../../../images/icons/video_product.svg'
import { ReactComponent as AudioIcon } from '../../../images/icons/audio_product.svg'
import ProductRadioInputCreativeUpload from '../../../components/FormElements/ProductRadioInputCreativeUpload'
import { upperCase } from 'lodash'
import toast from 'react-hot-toast'
import { useCreateLinkCampaign } from '../../../api/dealership/campaigns/useCreateLinkCampaign'
import moment from 'moment'
import { useCreateLinkCampaignCreative } from '../../../api/dealership/campaigns/useCreateLinkCampaignCreative'
import { useSignS3LinkForUpload } from '../../../api/utils/useSignS3LinkForUpload'
import { useDealership } from '../../../store/zustand/dealershipStore'
import md5 from 'md5'
import { useUploadFileToSignedLink } from '../../../api/utils/useUploadFileToSignedLink'
import CampaignProgressBar from '../../../components/CampaignProgressBars/CampaignProgressBar'
import { c } from 'react-querybuilder'
import classNames from 'classnames'

type Props = {
  step: number
  setStep: (step: number) => void
}

const CreateNonInventoryCampaignProducts = ({ step, setStep }: Props): JSX.Element => {
  const dealership = useDealership()
  const formContext = useFormContext()
  const initialMobCreatives = useMemo(() => {
    return [
      { src: '', file: undefined },
      { src: '', file: undefined },
      { src: '', file: undefined },
    ]
  }, [])
  const initialOttCreatives = useMemo(() => {
    return [{ src: '', file: undefined }]
  }, [])
  const initialVidCreatives = useMemo(() => {
    return [
      { src: '', file: undefined },
      { src: '', file: undefined },
    ]
  }, [])
  const initialAudCreatives = useMemo(() => {
    return [
      { src: '', file: undefined },
      { src: '', file: undefined },
      { src: '', file: undefined },
    ]
  }, [])
  const [containerHeight, setContainerHeight] = React.useState(0)
  const [mobileCreatives, setMobileCreatives] = useState(initialMobCreatives)
  const [ottCreatives, setOttCreatives] = useState(initialOttCreatives)
  const [vidCreatives, setVidCreatives] = useState(initialVidCreatives)
  const [audCreatives, setAudCreatives] = useState(initialAudCreatives)
  const [formValues, setFormValues] = useAtom(createCampaignFormValuesAtom)
  const { handleSubmit, setValue, watch } = formContext
  const formData = watch()

  const { mutateAsync: createS3LinkForUpload } = useSignS3LinkForUpload()
  const { mutateAsync: createLinkCampaign, isLoading } = useCreateLinkCampaign()
  const { mutateAsync: createLinkCampaignCreative } = useCreateLinkCampaignCreative()

  const { mutateAsync: uploadFileToSignedLink } = useUploadFileToSignedLink()

  const products = useMemo(
    () => ({
      mob: {
        product_code: 'mob',
        product_name: 'Mobile Display',
        product_description:
          'Reach and engage your target audience on the go with mobile advertising, capturing their attention through personalized and location-based targeting.',
        icon: <MobileIcon />,
      },
      ott: {
        product_code: 'ott',
        product_name: 'TV (OTT/CTV)',
        product_description: `Extend your campaign's reach beyond traditional TV channels and connect with viewers through Over-the-Top (OTT) advertising, delivering your message on streaming platforms to a highly engaged audience.`,
        icon: <OttIcon />,
      },
      vid: {
        product_code: 'vid',
        product_name: 'Video (Preroll)',
        product_description: `Drive brand awareness and engagement with video preroll ads, capturing viewers' attention before they watch their desired online video content and delivering your message effectively.`,
        icon: <VideoIcon />,
      },
      aud: {
        product_code: 'aud',
        product_name: 'Digital Audio',
        product_description: `Tap into the power of audio advertising, leveraging the popularity of podcasts, music streaming platforms, and digital radio to connect with listeners, enhance brand recall, and create a memorable auditory experience for your campaign.`,
        icon: <AudioIcon />,
      },
    }),
    [],
  )

  const onSubmit = useCallback(
    async (values: Partial<FieldValues>): Promise<void> => {
      const pacing_id = moment().unix()
      const s3Bucket = 'client-creative-images'
      const allCreatives = [
        ...mobileCreatives.filter((creative) => creative.src),
        ...ottCreatives.filter((creative) => creative.src),
        ...vidCreatives.filter((creative) => creative.src),
        ...audCreatives.filter((creative) => creative.src),
      ]
      const creativeImages: string[] = []

      if (!values.product) {
        toast.error('Please choose a product')
        return
      }

      if (allCreatives.length > 0) {
        allCreatives.forEach(async (creative: { src: string; file: any }) => {
          const fileName = `link/${
            process.env.NODE_ENV !== 'production' ? 'test/' : ''
          }${md5(String(dealership?.id))}/${md5(creative?.file?.name)}`
          creativeImages.push(`https://${s3Bucket}.s3.amazonaws.com/${fileName}`)
          // eslint-disable-next-line no-await-in-loop
          const s3UploadLink = await createS3LinkForUpload({
            data: {
              mimeType: creative?.file?.type,
              fileName: fileName,
              bucket_name: s3Bucket,
            },
          })
          uploadFileToSignedLink({
            signedRequest: s3UploadLink.uploadUrl,
            file: creative.file,
          })
        })
      }

      const linkCampaign = await createLinkCampaign({
        data: {
          pacing_id: pacing_id,
          product_code: upperCase(values.product),
          is_incomplete: 1,
          creatives: creativeImages.join(','),
        },
      })

      if (creativeImages.length > 0) {
        creativeImages.forEach(async (creative: string) => {
          await createLinkCampaignCreative({
            data: {
              link_campaign_id: linkCampaign.link_campaign_id,
              is_active: 1,
              creative_image: creative,
            },
          })
        })
      }

      setFormValues((prevFormValues) => {
        return {
          ...prevFormValues,
          pacing_id: pacing_id,
          campaign_id: linkCampaign.link_campaign_id,
          product: {
            ...products[values.product as keyof typeof products],
            creatives: creativeImages,
          },
        }
      })

      setStep(step + 1)
    },
    [
      audCreatives,
      createLinkCampaign,
      createLinkCampaignCreative,
      createS3LinkForUpload,
      dealership?.id,
      mobileCreatives,
      ottCreatives,
      setFormValues,
      setStep,
      step,
      uploadFileToSignedLink,
      vidCreatives,
      products,
    ],
  )

  useEffect(() => {
    if (formValues.product) setValue('product', formValues.product.product_code)
  }, [setValue, formValues, formData.product])

  // Reset the creatives on product change
  useEffect(() => {
    setMobileCreatives(formData.product === 'mob' ? mobileCreatives : initialMobCreatives)
    setOttCreatives(formData.product === 'ott' ? ottCreatives : initialOttCreatives)
    setVidCreatives(formData.product === 'vid' ? vidCreatives : initialVidCreatives)
    setAudCreatives(formData.product === 'aud' ? audCreatives : initialAudCreatives)
  }, [
    audCreatives,
    formData.product,
    initialAudCreatives,
    initialMobCreatives,
    initialOttCreatives,
    initialVidCreatives,
    mobileCreatives,
    ottCreatives,
    vidCreatives,
  ])

  const submitOnEnter = useCallback(
    (e): void => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSubmit(onSubmit)()
      }
    },
    [handleSubmit, onSubmit],
  )

  useEffect(() => {
    document.addEventListener('keydown', submitOnEnter)
    return () => document.removeEventListener('keydown', submitOnEnter)
  }, [submitOnEnter])

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      className={'flex flex-1 flex-col overflow-auto pb-20'}>
      <motion.div
        ref={(_ref) => {
          if (containerHeight === 0 && _ref?.clientHeight)
            setContainerHeight(_ref?.clientHeight ?? 0)
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={classNames('flex flex-1 flex-col pt-8 pb-8 overflow-auto', {
          'max-h-[auto]': containerHeight === 0,
          'max-h-full]': containerHeight !== 0,
        })}>
        {containerHeight > 0 && (
          <div className={'flex flex-col gap-5 px-12'}>
            <ProductRadioInputCreativeUpload
              name={'product'}
              product={products.mob}
              value={products.mob.product_code}
              creatives={mobileCreatives}
              setCreatives={setMobileCreatives}
            />
            <ProductRadioInputCreativeUpload
              name={'product'}
              product={products.ott}
              value={products.ott.product_code}
              creatives={ottCreatives}
              setCreatives={setOttCreatives}
              type={'video'}
            />
            <ProductRadioInputCreativeUpload
              name={'product'}
              product={products.vid}
              value={products.vid.product_code}
              creatives={vidCreatives}
              setCreatives={setVidCreatives}
              type={'video'}
            />
            <ProductRadioInputCreativeUpload
              name={'product'}
              product={products.aud}
              value={products.aud.product_code}
              creatives={audCreatives}
              setCreatives={setAudCreatives}
              type={'audio'}
            />
          </div>
        )}
      </motion.div>
      <motion.div
        layout
        layoutId={'buttons-container'}
        className={
          'h-20 items-center flex justify-between border-t-2 py-4 px-12 absolute bottom-0 w-full'
        }>
        <Button
          variant={'secondary'}
          onClick={(e) => {
            e.preventDefault()
            setStep(step - 1)
          }}>
          Back
        </Button>
        <CampaignProgressBar step={step} />
        <Button variant={'primary'} disabled={isLoading}>
          Continue
        </Button>
      </motion.div>
    </motion.form>
  )
}

export default CreateNonInventoryCampaignProducts
